import React, { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useAuth0 } from '@/hooks/useAuth0'
import { PermissionType } from '@/types/auth'
import { getRoutePermissions } from '@/config/routePermissions'
import { activationApi } from '@/services/activationApi'
import api from '@/services/api'

interface ProtectedRouteProps {
  children: React.ReactNode
  permissions?: PermissionType[]
  requireAllPermissions?: boolean
  fallback?: React.ReactNode
}

/**
 * Protected route wrapper that handles authentication and optional permission checks.
 *
 * @param children - The content to render if authorised
 * @param permissions - Optional array of permissions required to access the route
 * @param requireAllPermissions - If true, all permissions are required. If false, any permission is sufficient.
 * @param fallback - Optional component to show when permissions are denied (defaults to redirect)
 */
export default function ProtectedRoute({
  children,
  permissions,
  requireAllPermissions = false,
  fallback,
}: ProtectedRouteProps) {
  // Auth bypass for E2E tests (checked after hooks are declared)
  const isE2EBypass = import.meta.env.VITE_DISABLE_AUTH === 'true'

  const {
    user,
    loading: legacyLoading,
    hasAnyPermission,
    hasAllPermissions,
    subscriptionActive,
    hasAppAccess: userHasAppAccess,
    isClientAdmin,
    refreshUser,
  } = useAuth()
  const { isAuthenticated: isAuth0Authenticated, isLoading: auth0Loading, user: auth0User } = useAuth0()
  const location = useLocation()
  const [auth0Configured, setAuth0Configured] = useState<boolean | null>(null)

  // Token activation form state (must be declared before any early returns)
  const [showTokenForm, setShowTokenForm] = useState(false)
  const [token, setToken] = useState('')
  const [tokenError, setTokenError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Dev mode auth bypass - skip Auth0 checks entirely when dev mode session is active
  const isDevModeAuth = typeof window !== 'undefined' && !!localStorage.getItem('dev_mode_auth')
  // Dev environment bypass - skip subscription/access gates but still use Auth0 for login
  const isDevEnv = import.meta.env.VITE_DEV_MODE === 'true'

  // Check if Auth0 is configured (skip for dev mode sessions)
  useEffect(() => {
    if (isDevModeAuth) {
      setAuth0Configured(false)
      return
    }
    async function checkAuth0() {
      try {
        const response = await api.get('/auth/config')
        setAuth0Configured(!!response.data.domain)
      } catch {
        setAuth0Configured(false)
      }
    }
    checkAuth0()
  }, [isDevModeAuth])

  // E2E bypass (after all hooks)
  if (isE2EBypass) {
    return <>{children}</>
  }

  // Check if there's a token in localStorage (user might be authenticated but state not loaded yet)
  const hasAuth0Token = typeof window !== 'undefined' && !!localStorage.getItem('auth0_access_token')

  // Show loading spinner while checking auth
  // Skip Auth0 loading checks for dev mode sessions
  const loading = isDevModeAuth
    ? legacyLoading
    : legacyLoading || auth0Loading || auth0Configured === null || (hasAuth0Token && !isAuth0Authenticated)
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  // Check both legacy auth (user) and Auth0 auth (isAuth0Authenticated)
  const isAuthenticated = user || (auth0Configured && isAuth0Authenticated)
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // RBAC v1.2: Check subscription is active (bypassed in dev environments)
  if (subscriptionActive === false && !isDevModeAuth && !isDevEnv) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full text-center p-8">
          <div className="mx-auto h-16 w-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <svg className="h-8 w-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Subscription Inactive</h2>
          <p className="text-gray-600 mb-6">
            Your organisation's subscription is not active. Please contact your administrator to renew.
          </p>
        </div>
      </div>
    )
  }

  // RBAC v1.2: Check user has explicit app access (Client Admins bypass this; bypassed in dev environments)
  if (!userHasAppAccess && !isClientAdmin && !isDevModeAuth && !isDevEnv) {
    const handleTokenSubmit = async (e: React.FormEvent) => {
      e.preventDefault()
      setTokenError(null)

      const trimmedToken = token.trim()
      if (!trimmedToken) {
        setTokenError('Please enter your activation token.')
        return
      }

      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      if (!uuidRegex.test(trimmedToken)) {
        setTokenError('Invalid token format. The token should be a UUID (e.g. 12345678-abcd-1234-abcd-123456789012).')
        return
      }

      setIsSubmitting(true)
      try {
        const result = await activationApi.redeemToken({ token: trimmedToken })
        if (result.success) {
          await refreshUser()
        } else {
          setTokenError(result.error || 'Activation failed. Please check your token and try again.')
        }
      } catch {
        setTokenError('Failed to connect to the server. Please try again later.')
      } finally {
        setIsSubmitting(false)
      }
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full text-center p-8">
          <div className="mx-auto h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <svg className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Not Granted</h2>
          <p className="text-gray-600 mb-6">
            You do not have access to this application. Please ask your Client Admin to grant you access.
          </p>

          <div className="border-t border-gray-200 pt-4 mt-2">
            {!showTokenForm ? (
              <button
                onClick={() => setShowTokenForm(true)}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Have a Client Admin token?
              </button>
            ) : (
              <form onSubmit={handleTokenSubmit} className="space-y-3 text-left">
                <label htmlFor="activation-token" className="block text-sm font-medium text-gray-700">
                  Activation Token
                </label>
                <input
                  id="activation-token"
                  type="text"
                  placeholder="e.g. 12345678-abcd-1234-abcd-123456789012"
                  value={token}
                  onChange={(e) => { setToken(e.target.value); setTokenError(null) }}
                  disabled={isSubmitting}
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                />

                {tokenError && (
                  <div className="rounded-md bg-red-50 p-3">
                    <p className="text-sm text-red-700">{tokenError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm"
                >
                  {isSubmitting ? 'Activating...' : 'Activate'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    )
  }

  // RBAC v1.2: Check route-level permissions
  // Auto-resolve from ROUTE_PERMISSIONS config when no explicit permissions prop is passed
  // Note: isDevEnv does NOT bypass permission checks - only subscription/app access gates
  const resolvedPermissions = permissions ?? getRoutePermissions(location.pathname)
  if (resolvedPermissions.length > 0 && !isDevModeAuth) {
    const hasRequiredPermissions = requireAllPermissions
      ? hasAllPermissions(resolvedPermissions)
      : hasAnyPermission(resolvedPermissions)

    if (!hasRequiredPermissions) {
      // Show fallback or access denied page
      if (fallback) {
        return <>{fallback}</>
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full text-center p-8">
            <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg
                className="h-8 w-8 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Access Denied
            </h2>
            <p className="text-gray-600 mb-6">
              You do not have permission to access this page. Please contact
              your administrator if you believe this is an error.
            </p>
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      )
    }
  }

  return <>{children}</>
}

/**
 * Component that conditionally renders children based on permissions.
 * Does not redirect - just hides content when permissions are not met.
 */
interface RequirePermissionProps {
  children: React.ReactNode
  permissions: PermissionType[]
  requireAll?: boolean
  fallback?: React.ReactNode
}

export function RequirePermission({
  children,
  permissions,
  requireAll = false,
  fallback = null,
}: RequirePermissionProps) {
  const { hasAnyPermission, hasAllPermissions, user } = useAuth()

  if (!user) {
    return <>{fallback}</>
  }

  const hasPermission = requireAll
    ? hasAllPermissions(permissions)
    : hasAnyPermission(permissions)

  if (!hasPermission) {
    return <>{fallback}</>
  }

  return <>{children}</>
}

/**
 * Higher-order component for wrapping components with permission checks.
 */
// eslint-disable-next-line react-refresh/only-export-components -- HOC intentionally exported with component
export function withPermission<P extends object>(
  Component: React.ComponentType<P>,
  permissions: PermissionType[],
  requireAll = false
) {
  return function PermissionWrappedComponent(props: P) {
    return (
      <RequirePermission permissions={permissions} requireAll={requireAll}>
        <Component {...props} />
      </RequirePermission>
    )
  }
}
